<template>
  <div class="order">
    <div class="mb-5">
      <router-link
        :to="localizedRoute({ name: 'netsuite-orders' })"
        class="text-sm leading-none tracking-lg uppercase underline hover:no-underline text-dark"
      >
        {{ $t('Back to Order History') }}
      </router-link>
    </div>

    <h2 class="text-primary md:font-normal font-sans md:text-mid text-h5 leading-h5 md:italic md:font-serif md:tracking-tiny mb-5 uppercase text-center md:text-left md:normal-case font-bold tracking-lg">
      <span>{{ $t('Order') }}</span>
      <span v-if="orderNumber">{{ ` #${orderNumber}` }}</span>
      <small
        v-if="orderDate"
        class="lg:float-right block lg:inline"
      >{{ orderDate | localeDate }}</small>
      <small
        v-else-if="isLoading && !items.length"
        class="lg:float-right block lg:inline mt-2 lg:mt-0"
      >
        <span class="skeleton-bone skeleton-bone--sm skeleton-bone--w-sm inline-block" />
      </small>
    </h2>

    <template v-if="isLoading && !items.length">
      <div class="bg-white lg:py-xl lg:px-8.5 p-md mb-5">
        <div class="lg:flex lg:justify-end mb-10">
          <span class="skeleton-bone skeleton-bone--btn skeleton-bone--w-xl" />
        </div>
        <div class="lg:flex">
          <div class="mb-5 lg:mb-0 w-full lg:w-1/2">
            <div class="mb-sm">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-md" />
            </div>
            <div class="skeleton-stack">
              <span class="skeleton-bone skeleton-bone--sm skeleton-bone--w-xl" />
              <span class="skeleton-bone skeleton-bone--sm skeleton-bone--w-lg" />
              <span class="skeleton-bone skeleton-bone--sm skeleton-bone--w-md" />
              <span class="skeleton-bone skeleton-bone--sm skeleton-bone--w-lg" />
            </div>
          </div>
          <div class="w-full lg:w-1/2">
            <div class="mb-sm">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-sm" />
            </div>
            <div class="skeleton-stack">
              <span class="skeleton-bone skeleton-bone--sm skeleton-bone--w-lg" />
              <span class="skeleton-bone skeleton-bone--sm skeleton-bone--w-md" />
            </div>
          </div>
        </div>
      </div>

      <h2 class="text-primary md:font-normal font-sans md:text-mid text-h5 leading-none lg:leading-h5 md:italic md:font-serif md:tracking-tiny lg:my-10 uppercase text-center md:text-left md:normal-case font-bold tracking-lg my-5 py-5 border-b border-grey-38 lg:border-0">
        <span>{{ $t('Items ordered') }}</span>
      </h2>

      <div class="bg-white lg:py-xl lg:px-8.5 p-md">
        <table class="w-full my-10">
          <thead>
            <tr>
              <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden lg:table-cell">
                {{ $t('Product name') }}
              </th>
              <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden lg:table-cell">
                {{ $t('Sku') }}
              </th>
              <th class="text-right font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden lg:table-cell">
                {{ $t('Price') }}
              </th>
              <th class="text-right font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden lg:table-cell">
                {{ $t('QTY') }}
              </th>
              <th class="text-right font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden lg:table-cell">
                {{ $t('Subtotal') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="n in skeletonRowCount"
              :key="`skeleton-item-${n}`"
              class="skeleton-row block lg:table-row py-sm lg:py-0"
            >
              <td class="lg:pb-sm block lg:table-cell py-1">
                <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-xl" />
              </td>
              <td class="lg:pb-sm hidden lg:table-cell">
                <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-md" />
              </td>
              <td class="lg:pb-sm hidden lg:table-cell text-right">
                <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-xs ml-auto" />
              </td>
              <td class="lg:pb-sm hidden lg:table-cell text-right">
                <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-xxs ml-auto" />
              </td>
              <td class="lg:pb-sm hidden lg:table-cell text-right">
                <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-sm ml-auto" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <div
      v-else-if="error && !items.length"
      class="bg-white lg:py-xl lg:px-8.5 p-md"
    >
      <p class="leading-base tracking-mid">{{ error }}</p>
    </div>

    <template v-else>
      <div class="bg-white lg:py-xl lg:px-8.5 p-md mb-5">
        <div class="lg:flex lg:justify-end mb-10">
          <button-full
            class="w-full lg:w-auto print-button"
            @click.native="triggerPrint"
          >
            <span class="lg:text-sm text-h5">{{ $t('Print order') }}</span>
            <i class="ss-gizmo ss-print inline-block align-middle ml-1"></i>
          </button-full>
        </div>
        <div class="lg:flex">
          <div class="mb-5 lg:mb-0 w-full lg:w-1/2">
            <div class="mb-sm text-base font-bold leading-normal tracking-tiny">
              {{ $t('Order Details') }}
            </div>
            <div class="text-dark text-base tracking-xs leading-h5">
              <p v-if="customerName">{{ customerName }}</p>
              <p v-if="transactionType">{{ $t('Type') }}: {{ transactionType }}</p>
            </div>
          </div>
          <div class="w-full lg:w-1/2">
            <div class="mb-sm text-base font-bold leading-normal tracking-tiny">
              {{ $t('Summary') }}
            </div>
            <div class="text-dark text-base tracking-xs leading-h5">
              <p v-if="summaryTotal != null">
                {{ $t('Order Total') }}:
                <span class="italic font-serif">{{ Number(summaryTotal) | price }}</span>
              </p>
              <p>{{ $t('Line Items') }}: {{ items.length }}</p>
            </div>
          </div>
        </div>
      </div>

      <h2 class="text-primary md:font-normal font-sans md:text-mid text-h5 leading-none lg:leading-h5 md:italic md:font-serif md:tracking-tiny lg:my-10 uppercase text-center md:text-left md:normal-case font-bold tracking-lg my-5 py-5 border-b border-grey-38 lg:border-0">
        <span>{{ $t('Items ordered') }}</span>
      </h2>

      <div class="bg-white lg:py-xl lg:px-8.5 p-md">
        <table class="w-full my-10">
          <thead>
            <tr>
              <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden lg:table-cell">
                {{ $t('Product name') }}
              </th>
              <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden lg:table-cell">
                {{ $t('Sku') }}
              </th>
              <th class="text-right font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden lg:table-cell">
                {{ $t('Price') }}
              </th>
              <th class="text-right font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden lg:table-cell">
                {{ $t('QTY') }}
              </th>
              <th class="text-right font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden lg:table-cell">
                {{ $t('Subtotal') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(group, brandName) in groupedItems">
              <tr :key="`${brandName}-header`">
                <td
                  class="text-left text-above-md lg:text-base font-bold leading-none uppercase tracking-mid py-sm block lg:table-cell"
                  colspan="5"
                >
                  {{ brandName }}
                </td>
              </tr>
              <tr
                v-for="(item, index) in group"
                :key="`${brandName}-${item.item_name || index}`"
                class="block lg:table-row py-sm lg:py-0"
              >
                <td class="lg:pb-sm text-above-md lg:text-base leading-sm tracking-xs text-dark block lg:table-cell order__cell">
                  {{ item.item_displayname || item.item_name }}
                </td>
                <td class="lg:pb-sm text-above-md lg:text-base leading-sm tracking-xs text-dark block lg:table-cell order__cell">
                  {{ item.item_name }}
                </td>
                <td
                  class="lg:pb-sm text-above-md lg:text-base text-heading-page leading-h5 font-serif tracking-xs text-dark italic lg:text-right block lg:table-cell order__cell"
                  :data-content="$t('Price')"
                >
                  {{ unitPrice(item) | price }}
                </td>
                <td
                  class="lg:pb-sm text-above-md lg:text-base leading-sm tracking-xs text-dark lg:text-right block lg:table-cell order__cell"
                  :data-content="$t('Qty')"
                >
                  {{ Number(item.quantity || 0) }}
                </td>
                <td
                  class="lg:pb-sm text-above-md lg:text-base text-heading-page leading-h5 font-serif tracking-xs text-dark italic lg:text-right block lg:table-cell order__cell"
                  :data-content="$t('Total Price')"
                >
                  {{ Number(item.net_amount != null ? item.net_amount : item.amount || 0) | price }}
                </td>
              </tr>
              <tr :key="`${brandName}-subtotal`">
                <td
                  class="text-left text-above-md lg:text-base font-bold leading-none tracking-mid lg:text-right pb-md lg:pb-sm table-cell"
                  colspan="4"
                >
                  {{ $t(`${brandName} Subtotal`) }}
                </td>
                <td class="text-left text-above-md lg:text-base font-bold leading-none tracking-mid text-right pb-md lg:pb-sm table-cell">
                  {{ brandSubtotal(group) | price }}
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr>
              <td
                colspan="4"
                class="font-bold leading-none text-heading-page leading-normal text-black pb-sm pt-md text-right"
              >
                {{ $t('Grand Total') }}
              </td>
              <td
                colspan="1"
                class="text-heading-page leading-h5 font-serif tracking-xs text-dark underline italic text-right"
              >
                {{ grandTotal | price }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ButtonFull from 'theme/components/theme/ButtonFull'
import { localeDate } from 'theme/filters/locale-date'

export default {
  name: 'NetsuiteOrder',
  components: {
    ButtonFull
  },
  filters: {
    localeDate
  },
  data () {
    return {
      skeletonRowCount: 6
    }
  },
  computed: {
    ...mapGetters({
      items: 'netsuite/getOrderItems',
      summaries: 'netsuite/getOrderSummaries',
      isLoading: 'netsuite/isLoading',
      error: 'netsuite/getError'
    }),
    transactionId () {
      return this.$route.params.transactionId
    },
    summary () {
      return this.summaries.find(order => String(order.id) === String(this.transactionId)) || null
    },
    firstItem () {
      return this.items[0] || null
    },
    orderNumber () {
      if (this.summary && this.summary.etail_id) return this.summary.etail_id
      if (this.firstItem && this.firstItem.invoice) return this.firstItem.invoice
      return this.transactionId
    },
    orderDate () {
      if (this.summary && this.summary.order_date) return this.summary.order_date
      if (this.firstItem && this.firstItem.monthyear) return this.firstItem.monthyear
      return null
    },
    customerName () {
      return (this.firstItem && this.firstItem.customer_name) || ''
    },
    status () {
      return (this.firstItem && this.firstItem.status) || ''
    },
    transactionType () {
      const type = (this.firstItem && this.firstItem.transaction_type) || ''
      if (!type) return ''
      const labels = {
        CustInvc: 'Invoice',
        SalesReceipt: 'Sales Receipt',
        CashSale: 'Sales Receipt',
        Journal: 'Journal',
        CustPymt: 'Payment',
        CustCred: 'Credit Memo',
        CashRfnd: 'Cash Refund',
        Deposit: 'Deposit',
        SalesOrd: 'Sales Order'
      }
      return labels[type] || type
    },
    territory () {
      return (this.firstItem && this.firstItem.territory) || ''
    },
    summaryTotal () {
      if (this.summary && this.summary.order_total != null) return this.summary.order_total
      return this.grandTotal
    },
    groupedItems () {
      const groups = {}
      this.items.forEach(item => {
        const brand = item.brand || item.class_name || this.$t('Items')
        if (!groups[brand]) groups[brand] = []
        groups[brand].push(item)
      })
      return groups
    },
    grandTotal () {
      return this.items.reduce((sum, item) => {
        return sum + Number(item.net_amount != null ? item.net_amount : item.amount || 0)
      }, 0)
    }
  },
  watch: {
    transactionId: {
      immediate: true,
      handler (id) {
        if (!id) return
        this.$store.dispatch('netsuite/fetchOrderItems', id).catch(() => {})
      }
    }
  },
  beforeDestroy () {
    this.$store.dispatch('netsuite/clearOrderItems')
  },
  methods: {
    unitPrice (item) {
      const qty = Number(item.quantity || 0)
      const total = Number(item.net_amount != null ? item.net_amount : item.amount || 0)
      if (!qty) return total
      return total / qty
    },
    brandSubtotal (group) {
      return group.reduce((sum, item) => {
        return sum + Number(item.net_amount != null ? item.net_amount : item.amount || 0)
      }, 0)
    },
    triggerPrint () {
      if (window) window.print()
    }
  }
}
</script>

<style lang="scss" scoped>
.order {
  &__cell[data-content] {
    &:after {
      @apply inline-block float-left mr-xs;
      content: attr(data-content) ":";
      @screen lg {
        @apply hidden;
      }
    }
  }
}

.print-button {
  @apply py-3;
  @screen lg {
    @apply px-5 py-xs;
  }
}

.skeleton-stack > * + * {
  margin-top: 0.5rem;
}

.skeleton-bone {
  @apply block;
  border-radius: 2px;
  height: 0.875rem;
  max-width: 100%;
  background: linear-gradient(
    90deg,
    #ebebeb 0%,
    #f7f7f7 40%,
    #ebebeb 80%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;

  &--sm {
    height: 0.75rem;
  }

  &--md {
    height: 1rem;
  }

  &--btn {
    height: 2.5rem;
    @screen lg {
      height: 2rem;
    }
  }

  &--w-xxs {
    width: 2.5rem;
  }

  &--w-xs {
    width: 4.5rem;
  }

  &--w-sm {
    width: 6rem;
  }

  &--w-md {
    width: 7.5rem;
  }

  &--w-lg {
    width: 9rem;
  }

  &--w-xl {
    width: 12rem;
  }
}

.skeleton-row {
  &:nth-child(1) .skeleton-bone { animation-delay: 0ms; }
  &:nth-child(2) .skeleton-bone { animation-delay: 80ms; }
  &:nth-child(3) .skeleton-bone { animation-delay: 160ms; }
  &:nth-child(4) .skeleton-bone { animation-delay: 240ms; }
  &:nth-child(5) .skeleton-bone { animation-delay: 320ms; }
  &:nth-child(6) .skeleton-bone { animation-delay: 400ms; }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}
</style>
